import type { FunctionMeta } from '../function.model';

// =============================================================================
// PARAMS - Mathematical definition
// =============================================================================

/** Sine function parameters: a * sin(b(x - c)) + d */
export interface SineParams {
  /** Amplitude (vertical stretch) */
  a: number;
  /** Frequency (affects period = 2π/b) */
  b: number;
  /** Phase shift (horizontal translation) */
  c: number;
  /** Vertical shift */
  d: number;
}

// =============================================================================
// META - Function configuration (type-checked)
// =============================================================================

/**
 * Configuration metadata for sine functions.
 * Handle is placed at (c, d) which is the inflection point where sin(0) = 0.
 * This makes the handle visually sit on the curve at the midline.
 */
export const SINE_META: FunctionMeta<SineParams> = {
  availableFeatures: [
    'handle',
    'roots',
    'extrema',
    'tangent',
    'secant',
    'derivative',
    'axisOfSymmetry',
    'inflection',
    'intersection',
    'yIntercept',
    'trajectory',
    'monotonicity',
    'functionArea',
    'intersectionArea',
    'amplitude',
    'wavelength',
    'period',
    'phaseShift',
    'domain',
    'range',
  ],
  handleConfigs: {
    // For sine, (c, d) is already on the curve since sin(0) = 0
    // So the default mapping works correctly
    translate: { xParam: 'c', yParam: 'd' },
    'translate-x': { xParam: 'c', yParam: 'd' },
    'translate-y': { xParam: 'c', yParam: 'd' },
  },
  defaultParams: {
    a: 1,
    b: 1,
    c: 0,
    d: 0,
  },
  defaultRanges: {
    a: [-5, 5],
    b: [0.1, 5],
    c: [-10, 10],
    d: [-10, 10],
  },
};
